import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { DfEventService } from '@df/core/service/df-event';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { LoqateEmailValidateDirective } from '@df/module-loqate/email/loqate-email.validator';
import { LoqateService } from '@df/module-loqate/loqate.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { LoginFormModalAbstract } from '../../../login/form/modal/login-form-modal.abstract';
import { ActionComponent } from '../../../ui/api/action.component';
import { ResultComponent } from '../../../ui/api/result.component';
import { CheckboxComponent } from '../../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { MuiInputDirective } from '../../../ui/form/mui-input.directive';
import { ValidatePatternAsyncDirective } from '../../../ui/form/validate/validate-pattern-acync.directive';
import { ISignUpPopupComponentData } from '../sign-up-popup/sign-up-popup.interfaces';

export const SIGN_UP_POPUP_NAME = 'sign-up-popup';

@Component({
    selector: 'sign-up-popup-big',
    templateUrl: './sign-up-popup-big.html',
    styleUrl: './sign-up-popup-big.scss',
    standalone: true,
    imports: [
        ActionComponent,
        CheckboxComponent,
        CommonModule,
        DfImageDirective,
        ICmsClientModule,
        FormsModule,
        GridModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        LoqateEmailValidateDirective,
        MuiInputDirective,
        ResultComponent,
        RouterModule,
        SizeClassDirective,
        TranslatePipe,
        ValidatePatternAsyncDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignUpPopupBigComponent extends LoginFormModalAbstract {
    @Input()
    content!: ISignUpPopupComponentData;

    private localStorageService = inject(LocalStorageService);
    protected loqateService = inject(LoqateService);
    protected dfEventService = inject(DfEventService);
    protected override newsletterPlacement = 'login_modal';

    public isPending = false;

    ngAfterViewInit(): void {
        this.subscriptions.add(this.form?.statusChanges?.subscribe(event => this.updateClass(event)));
    }

    override handleKeyUp(event?: KeyboardEvent, form?: NgForm) {
        if (event?.key === 'Enter') {
            this.emailInput?.nativeElement?.blur();

            this.submit();
        }

        if (event && form && form['submitAttempt'] && !this.email?.length) {
            form['submitAttempt'] = false;

            this.detectChanges();
        } else if (!event && this.email?.length) {
            if (form && !form['submitAttempt']) {
                form['submitAttempt'] = true;
                form.ngSubmit.emit();

                this.check();
            }

            this.detectChanges();
        }
    }

    override submit(): void {
        const email = this.email;
        const form = this.form;

        if (form?.valid && email?.length && !this.isPending) {
            this.isPending = true;

            this.loqateService.validateEmail(email).then(result => {
                this.isPending = false;

                if (result && this.loqateService.getEmailError(result, email) === null) {
                    if (this.shouldLogin()) {
                        this.submitLogin();
                    } else if (this.shouldRegister()) {
                        this.submitRegister();
                    }
                } else {
                    this.checkStatus.error = 'LOQATE_INVALID';
                }
            });
        } else if (form) {
            form['submitAttempt'] = true;
            form.ngSubmit.emit();

            this.detectChanges();
        }
    }

    protected override onSubmitSuccess() {
        super.onSubmitSuccess();
        this.close();
    }

    override close() {
        this.localStorageService.set(SIGN_UP_POPUP_NAME, 1);
        super.close();
    }

    protected updateClass(event: 'PENDING' | 'INVALID' | 'VALID') {
        this.isPending = event === 'PENDING';

        this.detectChanges();
    }
}
